import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  MessageCircle,
  Send,
  Phone,
  User,
  Search,
  RefreshCw,
  CheckCheck,
  Check,
  Clock,
  Paperclip,
  Image,
  FileText,
  Link2,
  MoreVertical,
  ArrowLeft,
  UserPlus,
  Ticket,
  Bell,
  Volume2,
  Download,
  FileAudio,
  Video,
  X,
  XCircle,
  RotateCcw,
  StickyNote,
  Users,
  UserCheck,
  Archive,
  Inbox,
  Tag,
  Timer,
  Flame,
  AlertCircle,
  Zap,
  ChevronRight,
  ChevronLeft,
  Info,
  MessageSquare,
  Building2,
  Mail,
  Calendar,
  Plus,
  Trash2,
  Edit2,
  GitMerge,
  Globe,
  Lock,
  Wrench
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Default quick replies (fallback if none configured)
const DEFAULT_QUICK_REPLIES = [
  { reply_id: 'default_greeting', label: 'Hi', text: 'Hi! Thank you for reaching out. How can I help you today?', is_shared: true },
  { reply_id: 'default_thanks', label: 'Thanks', text: 'Thank you for your patience. Is there anything else I can help you with?', is_shared: true },
  { reply_id: 'default_wait', label: 'Please wait', text: 'Please wait while I check this for you.', is_shared: true },
  { reply_id: 'default_callback', label: 'Callback', text: 'I will have our team call you back shortly. Could you please confirm your contact number?', is_shared: true }
];

// Available tags for conversations
const AVAILABLE_TAGS = [
  { id: 'hot_lead', label: 'Hot Lead', color: 'bg-red-500' },
  { id: 'support', label: 'Support', color: 'bg-blue-500' },
  { id: 'follow_up', label: 'Follow-up', color: 'bg-amber-500' },
  { id: 'billing', label: 'Billing', color: 'bg-green-500' },
  { id: 'technical', label: 'Technical', color: 'bg-purple-500' },
  { id: 'vip', label: 'VIP', color: 'bg-pink-500' }
];

const MESSAGE_STATUS_ICONS = {
  sent: <Check className="h-3 w-3 text-slate-400" />,
  delivered: <CheckCheck className="h-3 w-3 text-slate-400" />,
  read: <CheckCheck className="h-3 w-3 text-blue-500" />,
  failed: <Clock className="h-3 w-3 text-red-500" />,
  received: null
};

// Notification sound
const notificationSound = typeof Audio !== 'undefined' ? new Audio('/notification.mp3') : null;

export default function WhatsAppInbox() {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [statusCounts, setStatusCounts] = useState({ all: 0, unattended: 0, assigned: 0, closed: 0 });
  
  // Message cache - store messages by phone number for instant loading
  const messageCache = useRef({});
  
  // Incremental polling - track last update timestamp
  const lastUpdateTimestamp = useRef(null);
  const isInitialLoadDone = useRef(false);
  
  // Dialogs
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(null);
  
  // Data
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  
  // UI State
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastUnreadCount, setLastUnreadCount] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [rightPanelTab, setRightPanelTab] = useState('info'); // 'info', 'notes', 'quick'
  const messagesEndRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const prevConversationPhoneRef = useRef(null);
  const fileInputRef = useRef(null);

  // Ticket creation state
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
    include_attachments: true
  });

  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Quick Replies state
  const [quickReplies, setQuickReplies] = useState({ personal: [], shared: [] });
  const [showQuickReplyForm, setShowQuickReplyForm] = useState(false);
  const [editingReply, setEditingReply] = useState(null);
  const [quickReplyForm, setQuickReplyForm] = useState({ label: '', text: '', is_shared: false });

  // Merge state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  const [selectedForMerge, setSelectedForMerge] = useState([]);
  const [mergeTarget, setMergeTarget] = useState(null);

  // Scroll to bottom of messages
  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Track whether user is near bottom so we don't yank them away while reading history
  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    // Treat within 80px of bottom as "at bottom"
    isAtBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
  };

  // Auto-scroll rules:
  //  - If conversation changed: jump to bottom (instant)
  //  - Else: only scroll if user was already at bottom (respect manual scroll-up)
  useEffect(() => {
    const currentPhone = selectedConversation?.phone || null;
    const conversationChanged = prevConversationPhoneRef.current !== currentPhone;
    prevConversationPhoneRef.current = currentPhone;

    if (conversationChanged) {
      isAtBottomRef.current = true;
      // Use rAF to wait for DOM render
      requestAnimationFrame(() => scrollToBottom('auto'));
      return;
    }
    if (isAtBottomRef.current) {
      scrollToBottom('smooth');
    }
  }, [messages, selectedConversation?.phone]);

  // Attach scroll listener to the shadcn ScrollArea viewport (Radix)
  useEffect(() => {
    const root = messagesScrollRef.current;
    if (!root) return;
    const viewport = root.querySelector('[data-radix-scroll-area-viewport]');
    if (!viewport) return;
    const handler = (e) => handleMessagesScroll({ currentTarget: e.currentTarget });
    viewport.addEventListener('scroll', handler, { passive: true });
    return () => viewport.removeEventListener('scroll', handler);
  }, [selectedConversation?.phone]);

  // Handle responsive view
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      if (permission === 'granted') {
        toast.success('Notifications enabled');
      }
    }
  };

  // Show browser notification
  const showNotification = useCallback((title, body) => {
    if (notificationsEnabled && 'Notification' in window) {
      new Notification(title, {
        body,
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png'
      });
    }
    if (soundEnabled && notificationSound) {
      notificationSound.play().catch(() => {});
    }
  }, [notificationsEnabled, soundEnabled]);

  // Fetch conversations - stable reference (full fetch for initial load & filter changes)
  const fetchConversations = useCallback(async (showNotif = false) => {
    try {
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const response = await fetch(`${API}/api/whatsapp/conversations?limit=100${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const convs = data.conversations || [];
        const counts = data.counts || { all: 0, unattended: 0, assigned: 0, closed: 0 };
        
        setConversations(convs);
        setStatusCounts(counts);
        
        // Save timestamp for incremental polling
        lastUpdateTimestamp.current = new Date().toISOString();
        
        // Persist to localStorage for instant load next time (only for 'all' filter)
        if (statusFilter === 'all') {
          try {
            localStorage.setItem('wa_conversations', JSON.stringify({
              conversations: convs,
              counts,
              timestamp: lastUpdateTimestamp.current
            }));
          } catch(e) { /* quota exceeded */ }
        }
        
        // Store unread count for notifications (handled separately)
        const newUnread = data.total_unread || 0;
        setLastUnreadCount(prev => {
          if (showNotif && newUnread > prev && prev > 0) {
            showNotification('New WhatsApp Message', `You have ${newUnread} unread messages`);
          }
          return newUnread;
        });
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token, statusFilter, showNotification]);

  // Incremental fetch - only get conversations updated since last poll
  const fetchUpdates = useCallback(async () => {
    if (!lastUpdateTimestamp.current) {
      return fetchConversations(true);
    }
    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/updates?since=${encodeURIComponent(lastUpdateTimestamp.current)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const updates = data.conversations || [];
        const newCounts = data.counts;
        const serverTimestamp = data.timestamp;

        if (newCounts) {
          setStatusCounts(newCounts);
        }

        if (updates.length > 0) {
          setConversations(prev => {
            const map = new Map(prev.map(c => [c.phone, c]));
            updates.forEach(u => map.set(u.phone, u));
            const merged = Array.from(map.values()).sort(
              (a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
            );
            // Persist merged state
            if (statusFilter === 'all') {
              try {
                localStorage.setItem('wa_conversations', JSON.stringify({
                  conversations: merged,
                  counts: newCounts,
                  timestamp: serverTimestamp
                }));
              } catch(e) {}
            }
            return merged;
          });

          // Check for new unread
          const newUnread = data.total_unread || 0;
          setLastUnreadCount(prev => {
            if (newUnread > prev && prev > 0) {
              showNotification('New WhatsApp Message', `You have ${newUnread} unread messages`);
            }
            return newUnread;
          });
        }

        lastUpdateTimestamp.current = serverTimestamp || new Date().toISOString();
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
    }
  }, [token, statusFilter, fetchConversations, showNotification]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/whatsapp/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  // Fetch messages for selected conversation - with caching for instant load
  const fetchMessages = useCallback(async (phone, forceRefresh = false) => {
    // Show cached messages immediately if available
    if (messageCache.current[phone] && !forceRefresh) {
      setMessages(messageCache.current[phone]);
    } else {
      setMessagesLoading(true);
    }
    
    try {
      const response = await fetch(`${API}/api/whatsapp/conversations/${phone}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const newMessages = data.messages || [];
        
        // Update cache
        messageCache.current[phone] = newMessages;
        setMessages(newMessages);
        
        // Update unread count in conversations
        setConversations(prev => prev.map(c => 
          c.phone === phone ? { ...c, unread_count: 0 } : c
        ));
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, [token]);

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/api/whatsapp/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Fetch employees for assignment
  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API}/api/whatsapp/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Fetch notes
  const fetchNotes = async (phone) => {
    try {
      const response = await fetch(`${API}/api/whatsapp/conversations/${phone}/notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  // Fetch quick replies
  const fetchQuickReplies = async () => {
    try {
      const response = await fetch(`${API}/api/whatsapp/quick-replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuickReplies({
          personal: data.personal || [],
          shared: data.shared || []
        });
      }
    } catch (error) {
      console.error('Error fetching quick replies:', error);
    }
  };

  // Create quick reply
  const handleCreateQuickReply = async () => {
    if (!quickReplyForm.label.trim() || !quickReplyForm.text.trim()) {
      toast.error('Please fill in both label and text');
      return;
    }

    try {
      const response = await fetch(`${API}/api/whatsapp/quick-replies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(quickReplyForm)
      });

      if (response.ok) {
        toast.success('Quick reply created');
        setQuickReplyForm({ label: '', text: '', is_shared: false });
        setShowQuickReplyForm(false);
        fetchQuickReplies();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create quick reply');
      }
    } catch (error) {
      toast.error('Failed to create quick reply');
    }
  };

  // Update quick reply
  const handleUpdateQuickReply = async () => {
    if (!editingReply) return;

    try {
      const response = await fetch(`${API}/api/whatsapp/quick-replies/${editingReply.reply_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(quickReplyForm)
      });

      if (response.ok) {
        toast.success('Quick reply updated');
        setEditingReply(null);
        setQuickReplyForm({ label: '', text: '', is_shared: false });
        fetchQuickReplies();
      } else {
        toast.error('Failed to update quick reply');
      }
    } catch (error) {
      toast.error('Failed to update quick reply');
    }
  };

  // Delete quick reply
  const handleDeleteQuickReply = async (replyId) => {
    if (!confirm('Delete this quick reply?')) return;

    try {
      const response = await fetch(`${API}/api/whatsapp/quick-replies/${replyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Quick reply deleted');
        fetchQuickReplies();
      } else {
        toast.error('Failed to delete quick reply');
      }
    } catch (error) {
      toast.error('Failed to delete quick reply');
    }
  };

  // Fetch merge suggestions
  const fetchMergeSuggestions = async () => {
    try {
      const response = await fetch(`${API}/api/whatsapp/merge-suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMergeSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching merge suggestions:', error);
    }
  };

  // Merge conversations
  const handleMergeConversations = async () => {
    if (!mergeTarget || selectedForMerge.length === 0) {
      toast.error('Please select a target and at least one conversation to merge');
      return;
    }

    try {
      const response = await fetch(`${API}/api/whatsapp/merge-conversations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_phone: mergeTarget,
          source_phones: selectedForMerge.filter(p => p !== mergeTarget)
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Merged ${data.merged_messages} messages from ${data.merged_phones.length - 1} conversations`);
        setShowMergeDialog(false);
        setSelectedForMerge([]);
        setMergeTarget(null);
        fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to merge conversations');
      }
    } catch (error) {
      toast.error('Failed to merge conversations');
    }
  };

  // Fix stuck conversations (Admin only)
  const handleFixStuckConversations = async () => {
    try {
      const response = await fetch(`${API}/api/whatsapp/admin/fix-stuck-conversations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.fixed_count > 0) {
          toast.success(`Fixed ${data.fixed_count} stuck conversations`);
          fetchConversations();
          fetchStats();
        } else {
          toast.info('No stuck conversations found');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fix conversations');
      }
    } catch (error) {
      toast.error('Failed to fix conversations');
    }
  };

  // Initial load - use localStorage cache for instant display, then fetch fresh
  useEffect(() => {
    const loadData = async () => {
      // Try localStorage cache first for instant render
      try {
        const cached = localStorage.getItem('wa_conversations');
        if (cached) {
          const { conversations: cachedConvs, counts: cachedCounts, timestamp } = JSON.parse(cached);
          if (cachedConvs && cachedConvs.length > 0) {
            setConversations(cachedConvs);
            setStatusCounts(cachedCounts || { all: 0, unattended: 0, assigned: 0, closed: 0 });
            lastUpdateTimestamp.current = timestamp;
            setLoading(false); // Show cached data immediately
            isInitialLoadDone.current = true;
          }
        }
      } catch(e) { /* invalid cache */ }

      // Fetch fresh data in background
      await Promise.all([fetchConversations(), fetchStats(), fetchQuickReplies()]);
      isInitialLoadDone.current = true;
      setLoading(false);
    };
    loadData();

    // Check notification permission
    if ('Notification' in window && Notification.permission === 'granted') {
      setNotificationsEnabled(true);
    }
  }, []); // Run once on mount

  // Incremental polling - uses /updates endpoint and skips when tab is hidden
  useEffect(() => {
    const pollInterval = setInterval(() => {
      // Skip polling when tab is not visible
      if (document.hidden) return;
      
      fetchUpdates();
      if (selectedConversation) {
        fetchMessages(selectedConversation.phone);
      }
    }, 5000);

    // When tab becomes visible again, do an immediate incremental fetch
    const handleVisibilityChange = () => {
      if (!document.hidden && isInitialLoadDone.current) {
        fetchUpdates();
        if (selectedConversation) {
          fetchMessages(selectedConversation.phone);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedConversation?.phone, fetchUpdates]); // Only restart when selected conversation changes

  // Refetch when status filter changes - reset incremental timestamp to force full fetch
  useEffect(() => {
    lastUpdateTimestamp.current = null;
    fetchConversations();
  }, [statusFilter]);

  // Select conversation - instant load from cache, then refresh in background
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    
    // Show cached messages instantly if available
    if (messageCache.current[conversation.phone]) {
      setMessages(messageCache.current[conversation.phone]);
    }
    
    // Fetch fresh messages (will update cache and show new ones)
    fetchMessages(conversation.phone);
  };

  // Send message - optimistic update for instant feedback
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const messageText = newMessage;
    const phone = selectedConversation.phone;
    
    // Optimistic update - add message to UI immediately
    const optimisticMessage = {
      message_id: `temp_${Date.now()}`,
      phone: phone,
      content: messageText,
      type: 'text',
      direction: 'outgoing',
      status: 'sending',
      timestamp: new Date().toISOString(),
      sent_by_name: user?.name || user?.email
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    messageCache.current[phone] = [...(messageCache.current[phone] || []), optimisticMessage];
    setNewMessage('');

    setSending(true);
    try {
      const response = await fetch(`${API}/api/whatsapp/send/text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_phone: phone,
          message: messageText
        })
      });

      if (response.ok) {
        // Refresh to get actual message with server-side ID
        fetchMessages(phone, true);
        // Update conversation in-place instead of full refetch
        setConversations(prev => prev.map(c =>
          c.phone === phone
            ? { ...c, last_message: messageText, last_message_at: new Date().toISOString(), status: c.status === 'unattended' ? 'assigned' : c.status }
            : c
        ));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
        // Remove optimistic message on failure
        setMessages(prev => prev.filter(m => m.message_id !== optimisticMessage.message_id));
        setNewMessage(messageText); // Restore the message
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      // Remove optimistic message on failure
      setMessages(prev => prev.filter(m => m.message_id !== optimisticMessage.message_id));
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  // Upload and send media
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedConversation) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('to_phone', selectedConversation.phone);
    formData.append('caption', '');

    try {
      const response = await fetch(`${API}/api/whatsapp/upload-media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Media sent successfully');
        await fetchMessages(selectedConversation.phone);
        await fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send media');
      }
    } catch (error) {
      console.error('Error uploading media:', error);
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Send template message
  const handleSendTemplate = async () => {
    if (!selectedTemplate || !selectedConversation) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/api/whatsapp/send/template`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to_phone: selectedConversation.phone,
          template_name: selectedTemplate.name,
          language_code: selectedTemplate.language || 'en'
        })
      });

      if (response.ok) {
        toast.success('Template message sent');
        setShowTemplateDialog(false);
        setSelectedTemplate(null);
        await fetchMessages(selectedConversation.phone);
        await fetchConversations();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send template');
      }
    } catch (error) {
      toast.error('Failed to send template');
    } finally {
      setSending(false);
    }
  };

  // Fetch customers for linking
  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  // Link customer
  const handleLinkCustomer = async (customerId) => {
    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/link-customer?customer_id=${customerId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Linked to ${data.customer_name}`);
        setShowLinkDialog(false);
        fetchConversations();
        setSelectedConversation(prev => ({
          ...prev,
          customer_id: customerId,
          customer_name: data.customer_name
        }));
      }
    } catch (error) {
      toast.error('Failed to link customer');
    }
  };

  // Assign conversation
  const handleAssign = async (userId) => {
    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/assign`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ assigned_to: userId })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Assigned to ${data.assigned_to_name}`);
        setShowAssignDialog(false);
        // Optimistic update
        setSelectedConversation(prev => ({
          ...prev,
          assigned_to: userId,
          assigned_to_name: data.assigned_to_name,
          status: 'assigned'
        }));
        setConversations(prev => prev.map(c =>
          c.phone === selectedConversation.phone
            ? { ...c, assigned_to: userId, assigned_to_name: data.assigned_to_name, status: 'assigned' }
            : c
        ));
        fetchConversations();
      }
    } catch (error) {
      toast.error('Failed to assign conversation');
    }
  };

  // Close conversation
  const handleCloseConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/close`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Conversation closed');
        // Optimistic update
        setSelectedConversation(prev => ({ ...prev, status: 'closed' }));
        setConversations(prev => prev.map(c =>
          c.phone === selectedConversation.phone ? { ...c, status: 'closed' } : c
        ));
        fetchConversations();
      }
    } catch (error) {
      toast.error('Failed to close conversation');
    }
  };

  // Reopen conversation
  const handleReopenConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/reopen`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Conversation reopened');
        const newStatus = selectedConversation.assigned_to ? 'assigned' : 'unattended';
        // Optimistic update
        setSelectedConversation(prev => ({ ...prev, status: newStatus }));
        setConversations(prev => prev.map(c =>
          c.phone === selectedConversation.phone ? { ...c, status: newStatus } : c
        ));
        fetchConversations();
      }
    } catch (error) {
      toast.error('Failed to reopen conversation');
    }
  };

  // Add internal note
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedConversation) return;

    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/notes`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note: newNote })
        }
      );

      if (response.ok) {
        toast.success('Note added');
        setNewNote('');
        fetchNotes(selectedConversation.phone);
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // Create ticket from chat
  const handleCreateTicket = async () => {
    if (!ticketForm.subject.trim()) {
      toast.error('Please enter a ticket subject');
      return;
    }

    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/create-ticket`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(ticketForm)
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(`Ticket ${data.ticket_number} created`);
        setShowTicketDialog(false);
        setTicketForm({
          subject: '',
          description: '',
          category: 'general',
          priority: 'medium',
          include_attachments: true
        });
        fetchConversations();
        setSelectedConversation(prev => ({
          ...prev,
          linked_ticket: data.ticket_number
        }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create ticket');
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  // Generate ticket description from messages
  const generateTicketDescription = () => {
    if (!messages.length) return '';
    
    const recentMessages = messages.slice(-10).map(msg => {
      const sender = msg.direction === 'incoming' 
        ? (selectedConversation?.contact_name || 'Customer') 
        : (msg.sent_by_name || 'Agent');
      return `${sender}: ${msg.content}`;
    }).join('\n');
    
    return `Chat Summary:\n${recentMessages}`;
  };

  // Get media content (with caching)
  const [mediaCache, setMediaCache] = useState({});
  
  const getMediaContent = async (mediaId) => {
    if (mediaCache[mediaId]) return mediaCache[mediaId];
    
    try {
      const response = await fetch(`${API}/api/whatsapp/media/${mediaId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const content = data.content;
        setMediaCache(prev => ({ ...prev, [mediaId]: content }));
        return content;
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    }
    return null;
  };

  // Format time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Filter conversations
  const filteredConversations = conversations.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      c.phone?.includes(searchTerm) ||
      c.contact_name?.toLowerCase().includes(searchLower) ||
      c.customer_name?.toLowerCase().includes(searchLower) ||
      c.assigned_to_name?.toLowerCase().includes(searchLower) ||
      c.last_message?.toLowerCase().includes(searchLower)
    );
    
    const matchesTag = tagFilter === 'all' || (c.tags && c.tags.includes(tagFilter));
    
    return matchesSearch && matchesTag;
  });

  // Add/Remove tag
  const handleToggleTag = async (tagId) => {
    if (!selectedConversation) return;
    
    const currentTags = selectedConversation.tags || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter(t => t !== tagId)
      : [...currentTags, tagId];
    
    try {
      const response = await fetch(
        `${API}/api/whatsapp/conversations/${selectedConversation.phone}/tags`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ tags: newTags })
        }
      );

      if (response.ok) {
        toast.success('Tags updated');
        setSelectedConversation(prev => ({ ...prev, tags: newTags }));
        fetchConversations();
      }
    } catch (error) {
      toast.error('Failed to update tags');
    }
  };

  // Calculate SLA info
  const getSLAInfo = (conv) => {
    if (!conv.first_message_at) return null;
    
    const firstMsgTime = new Date(conv.first_message_at);
    const firstResponseTime = conv.first_response_at ? new Date(conv.first_response_at) : null;
    const now = new Date();
    
    if (firstResponseTime) {
      // Calculate response time
      const responseMs = firstResponseTime - firstMsgTime;
      const responseMins = Math.floor(responseMs / 60000);
      if (responseMins < 60) {
        return { text: `${responseMins}m`, color: responseMins < 15 ? 'text-green-600' : 'text-amber-600' };
      }
      const responseHrs = Math.floor(responseMins / 60);
      return { text: `${responseHrs}h`, color: responseHrs < 1 ? 'text-amber-600' : 'text-red-600' };
    } else if (conv.status !== 'closed') {
      // Waiting for response
      const waitingMs = now - firstMsgTime;
      const waitingMins = Math.floor(waitingMs / 60000);
      if (waitingMins < 60) {
        return { text: `${waitingMins}m waiting`, color: waitingMins < 15 ? 'text-green-600' : 'text-red-600', waiting: true };
      }
      const waitingHrs = Math.floor(waitingMins / 60);
      return { text: `${waitingHrs}h waiting`, color: 'text-red-600', waiting: true };
    }
    return null;
  };

  // Get initials
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'unattended':
        return <Badge className="bg-red-500 text-white text-xs">Unattended</Badge>;
      case 'assigned':
        return <Badge className="bg-blue-500 text-white text-xs">Assigned</Badge>;
      case 'closed':
        return <Badge className="bg-slate-500 text-white text-xs">Closed</Badge>;
      default:
        return <Badge className="bg-amber-500 text-white text-xs">New</Badge>;
    }
  };

  // Media Message Component - Images load on click only (not auto-loaded)
  const MediaMessage = ({ msg }) => {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const loadMedia = async () => {
      if (!msg.media_id || content || loading) return;
      setLoading(true);
      try {
        const data = await getMediaContent(msg.media_id);
        setContent(data);
      } catch (error) {
        console.error('Failed to load media:', error);
      }
      setLoading(false);
    };

    const handleDownload = async () => {
      if (!msg.media_id || downloading) return;
      setDownloading(true);
      
      try {
        const response = await fetch(`${API}/api/whatsapp/media/${msg.media_id}/proxy`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const contentType = response.headers.get('content-type') || 'application/octet-stream';
          let extension = 'bin';
          if (contentType.includes('video')) extension = 'mp4';
          else if (contentType.includes('audio')) extension = 'mp3';
          else if (contentType.includes('image/png')) extension = 'png';
          else if (contentType.includes('image/jpeg')) extension = 'jpg';
          else if (contentType.includes('pdf')) extension = 'pdf';
          
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `whatsapp_${msg.type}_${msg.media_id}.${extension}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
          toast.success('Download started');
        } else {
          toast.error('Failed to download media');
        }
      } catch (error) {
        console.error('Download error:', error);
        toast.error('Failed to download media');
      } finally {
        setDownloading(false);
      }
    };

    // NO auto-load - images load only when user clicks
    if (msg.type === 'image') {
      return (
        <div className="mb-1">
          {loading ? (
            <div className="w-48 h-32 bg-slate-200 animate-pulse rounded flex items-center justify-center">
              <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : content ? (
            <img
              src={`data:image/jpeg;base64,${content}`}
              alt="Shared image"
              className="max-w-48 max-h-48 rounded cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => setShowImagePreview(`data:image/jpeg;base64,${content}`)}
            />
          ) : (
            <div 
              className="w-48 h-32 bg-gradient-to-br from-slate-100 to-slate-200 rounded flex items-center justify-center cursor-pointer hover:from-slate-200 hover:to-slate-300 transition-all border border-slate-200"
              onClick={loadMedia}
            >
              <div className="text-center">
                <Image className="h-8 w-8 text-green-600 mx-auto mb-1" />
                <span className="text-xs text-slate-600 font-medium">View Image</span>
              </div>
            </div>
          )}
          {msg.content && <p className="text-sm mt-1">{msg.content}</p>}
        </div>
      );
    }

    // Document, Audio, Video
    const icons = {
      document: FileText,
      audio: FileAudio,
      video: Video
    };
    const Icon = icons[msg.type] || FileText;

    return (
      <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg mb-1 border border-slate-200">
        <div className="p-2 bg-white rounded-full">
          <Icon className="h-5 w-5 text-green-600" />
        </div>
        <div className="flex-1">
          <span className="text-sm font-medium text-slate-700 capitalize">{msg.type}</span>
          {msg.content && <p className="text-xs text-slate-500">{msg.content}</p>}
        </div>
        {msg.media_id && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
            className="hover:bg-green-50 hover:border-green-300"
          >
            {downloading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Download className="h-4 w-4 mr-1" />
                <span className="text-xs">Download</span>
              </>
            )}
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100" data-testid="whatsapp-inbox">
      {/* Compact Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#075e54] text-white shadow-md">
        <div className="flex items-center gap-3">
          <MessageCircle className="h-6 w-6" />
          <div>
            <h1 className="text-lg font-semibold">WhatsApp Inbox</h1>
            <p className="text-xs text-green-200">
              {stats.total_customers || 0} customers • {stats.unread_count || 0} unread • {stats.status_counts?.unattended || 0} waiting
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* Quick Stats */}
          <div className="hidden md:flex items-center gap-3 mr-4 text-sm">
            <span className="flex items-center gap-1 text-green-200">
              <Users className="h-4 w-4" />
              {stats.active_today || 0} today
            </span>
            <span className="flex items-center gap-1 text-green-200">
              <Flame className="h-4 w-4" />
              {stats.new_today || 0} new
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-white hover:bg-white/20"
          >
            <Volume2 className={`h-4 w-4 ${soundEnabled ? '' : 'opacity-50'}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={requestNotificationPermission}
            className="text-white hover:bg-white/20"
          >
            <Bell className={`h-4 w-4 ${notificationsEnabled ? '' : 'opacity-50'}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { fetchConversations(); fetchStats(); }}
            className="text-white hover:bg-white/20"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {/* Admin: Fix Stuck Conversations */}
          {user?.roles?.includes('admin') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleFixStuckConversations}
              className="text-white hover:bg-white/20"
              title="Fix stuck conversations"
            >
              <Wrench className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <div className={`${isMobileView && selectedConversation ? 'hidden' : 'flex'} flex-col w-full md:w-96 bg-white border-r`}>
          {/* Status Tabs */}
          <div className="p-2 border-b bg-slate-50">
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="w-full grid grid-cols-4 bg-slate-200">
                <TabsTrigger value="all" className="text-xs px-1 data-[state=active]:bg-white">
                  All ({statusCounts.all})
                </TabsTrigger>
                <TabsTrigger value="unattended" className="text-xs px-1 data-[state=active]:bg-white data-[state=active]:text-red-600">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  New ({statusCounts.unattended})
                </TabsTrigger>
                <TabsTrigger value="assigned" className="text-xs px-1 data-[state=active]:bg-white">
                  Active ({statusCounts.assigned})
                </TabsTrigger>
                <TabsTrigger value="closed" className="text-xs px-1 data-[state=active]:bg-white">
                  Closed ({statusCounts.closed})
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {statusFilter === 'unattended' && statusCounts.unattended > 0 && (user?.role === 'admin' || user?.role === 'manager') && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Close all ${statusCounts.unattended} unattended conversations? This cannot be undone in bulk.`)) return;
                  try {
                    const r = await fetch(`${API}/api/whatsapp/admin/bulk-close-unattended`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (r.ok) {
                      const d = await r.json();
                      toast.success(`Closed ${d.closed_count} conversations`);
                      fetchConversations();
                    } else { toast.error('Failed to bulk close'); }
                  } catch { toast.error('Network error'); }
                }}
                className="mt-2 w-full text-[11px] py-1.5 rounded-md bg-red-50 hover:bg-red-100 text-red-700 font-medium border border-red-200 transition"
                data-testid="bulk-close-unattended-btn"
              >
                Close all {statusCounts.unattended} unattended
              </button>
            )}
          </div>

          {/* Tag Filter */}
          <div className="px-3 py-2 border-b bg-slate-50 flex items-center gap-2 overflow-x-auto">
            <Tag className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <Badge 
              variant={tagFilter === 'all' ? 'default' : 'outline'} 
              className="cursor-pointer text-xs flex-shrink-0"
              onClick={() => setTagFilter('all')}
            >
              All
            </Badge>
            {AVAILABLE_TAGS.map(tag => (
              <Badge 
                key={tag.id}
                variant={tagFilter === tag.id ? 'default' : 'outline'} 
                className={`cursor-pointer text-xs flex-shrink-0 ${tagFilter === tag.id ? tag.color + ' text-white' : ''}`}
                onClick={() => setTagFilter(tag.id)}
              >
                {tag.label}
              </Badge>
            ))}
          </div>

          {/* Search */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search name, phone, message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-8"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>
          </div>

          {/* Conversation List */}
          <ScrollArea className="flex-1">
            {filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-slate-500">
                <MessageCircle className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No conversations</p>
                <p className="text-xs mt-1">
                  {statusFilter !== 'all' ? `No ${statusFilter} chats` : 'Messages will appear here'}
                </p>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <div
                  key={conv.phone}
                  onClick={() => handleSelectConversation(conv)}
                  className={`group relative flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 border-b transition-colors ${
                    selectedConversation?.phone === conv.phone ? 'bg-green-50' : ''
                  }`}
                  data-testid={`conversation-${conv.phone}`}
                >
                  <Avatar className="h-10 w-10 bg-green-100 flex-shrink-0">
                    <AvatarFallback className="bg-green-100 text-green-700 text-sm">
                      {getInitials(conv.contact_name || conv.customer_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm truncate">
                        {conv.contact_name || conv.customer_name || conv.phone}
                      </span>
                      <div className="flex items-center gap-1">
                        {/* SLA Indicator */}
                        {(() => {
                          const sla = getSLAInfo(conv);
                          if (sla && sla.waiting) {
                            return (
                              <span className={`text-xs font-medium ${sla.color} flex items-center gap-0.5`}>
                                <Timer className="h-3 w-3" />
                                {sla.text}
                              </span>
                            );
                          }
                          return null;
                        })()}
                        <span className="text-xs text-slate-400">
                          {formatTime(conv.last_message_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-xs text-slate-500 truncate max-w-[180px]">
                        {conv.last_message || 'No messages'}
                      </span>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-green-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                    {/* Tags, Status & Assignment */}
                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                      {conv.tags && conv.tags.map(tagId => {
                        const tag = AVAILABLE_TAGS.find(t => t.id === tagId);
                        return tag ? (
                          <Badge key={tagId} className={`${tag.color} text-white text-[10px] py-0 px-1`}>
                            {tag.label}
                          </Badge>
                        ) : null;
                      })}
                      {getStatusBadge(conv.status)}
                      {conv.assigned_to_name && (
                        <Badge variant="outline" className="text-[10px] py-0 bg-blue-50 text-blue-700 border-blue-200">
                          {conv.assigned_to_name.split('@')[0]}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Quick close button — visible on hover, only for non-closed conversations */}
                  {conv.status !== 'closed' && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const r = await fetch(`${API}/api/whatsapp/conversations/${conv.phone}/close`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` },
                          });
                          if (r.ok) {
                            toast.success('Conversation closed');
                            setConversations(prev => prev.map(c => c.phone === conv.phone ? { ...c, status: 'closed' } : c));
                            if (selectedConversation?.phone === conv.phone) {
                              setSelectedConversation(prev => ({ ...prev, status: 'closed' }));
                            }
                            fetchConversations();
                          } else {
                            toast.error('Failed to close');
                          }
                        } catch { toast.error('Network error'); }
                      }}
                      className="absolute right-2 top-2 hidden group-hover:flex items-center justify-center h-6 w-6 rounded-full bg-white border border-slate-200 hover:bg-red-50 hover:border-red-300 text-slate-400 hover:text-red-600 transition"
                      title="Close conversation"
                      data-testid={`row-close-${conv.phone}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className={`${isMobileView && !selectedConversation ? 'hidden' : 'flex'} flex-1 overflow-hidden`}>
          {/* Main Chat Column */}
          <div className="flex flex-col flex-1 bg-slate-100">
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center gap-3 p-3 bg-[#f0f2f5] border-b">
                {isMobileView && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedConversation(null)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <Avatar className="h-10 w-10 bg-green-100">
                  <AvatarFallback className="bg-green-100 text-green-700">
                    {getInitials(selectedConversation.contact_name || selectedConversation.customer_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {selectedConversation.contact_name || selectedConversation.customer_name || 'Unknown'}
                    {getStatusBadge(selectedConversation.status)}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Phone className="h-3 w-3" />
                    +{selectedConversation.phone}
                    {selectedConversation.assigned_to_name && (
                      <span className="text-blue-600">• {selectedConversation.assigned_to_name.split('@')[0]}</span>
                    )}
                  </div>
                  {/* Tags in header */}
                  {selectedConversation.tags && selectedConversation.tags.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      {selectedConversation.tags.map(tagId => {
                        const tag = AVAILABLE_TAGS.find(t => t.id === tagId);
                        return tag ? (
                          <Badge key={tagId} className={`${tag.color} text-white text-[10px] py-0 px-1`}>
                            {tag.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
                
                {/* Actions Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="chat-menu-btn">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { fetchCustomers(); setShowLinkDialog(true); }}>
                      <Link2 className="h-4 w-4 mr-2" />
                      {selectedConversation.customer_id ? 'Change Customer' : 'Link Customer'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { fetchEmployees(); setShowAssignDialog(true); }}>
                      <UserPlus className="h-4 w-4 mr-2" />
                      {selectedConversation.assigned_to ? 'Reassign' : 'Assign'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { fetchNotes(selectedConversation.phone); setShowNotesDialog(true); }}>
                      <StickyNote className="h-4 w-4 mr-2" />
                      Internal Notes
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowTagDialog(true)}>
                      <Tag className="h-4 w-4 mr-2" />
                      Manage Tags
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { 
                      setTicketForm(prev => ({
                        ...prev,
                        subject: `WhatsApp inquiry from ${selectedConversation.contact_name || selectedConversation.phone}`,
                        description: generateTicketDescription()
                      }));
                      setShowTicketDialog(true);
                    }}>
                      <Ticket className="h-4 w-4 mr-2" />
                      Create Ticket
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { fetchTemplates(); setShowTemplateDialog(true); }}>
                      <FileText className="h-4 w-4 mr-2" />
                      Send Template
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { 
                      fetchMergeSuggestions();
                      setSelectedForMerge([selectedConversation.phone]);
                      setMergeTarget(selectedConversation.phone);
                      setShowMergeDialog(true);
                    }}>
                      <GitMerge className="h-4 w-4 mr-2" />
                      Merge Numbers
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {selectedConversation.status === 'closed' ? (
                      <DropdownMenuItem onClick={handleReopenConversation}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reopen Chat
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={handleCloseConversation} className="text-red-600">
                        <XCircle className="h-4 w-4 mr-2" />
                        Close Chat
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Messages */}
              <ScrollArea 
                ref={messagesScrollRef}
                className="flex-1 p-4" 
                style={{
                  backgroundColor: '#efeae2',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d1cdc7\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
                }}
              >
                <div className="space-y-2">
                  {messages.map((msg, idx) => (
                    <div
                      key={msg.message_id || idx}
                      className={`flex ${msg.direction === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 shadow-md ${
                          msg.direction === 'outgoing'
                            ? 'bg-[#d9fdd3] rounded-br-none'
                            : 'bg-white rounded-bl-none'
                        }`}
                      >
                        {/* Sender name for outgoing messages */}
                        {msg.direction === 'outgoing' && msg.sent_by_name && (
                          <div className="text-xs font-medium text-[#075e54] mb-1">
                            {msg.sent_by_name.split('@')[0]}
                          </div>
                        )}
                        
                        {/* Media content */}
                        {['image', 'document', 'audio', 'video'].includes(msg.type) && (
                          <MediaMessage msg={msg} />
                        )}
                        
                        {/* Text content */}
                        {msg.type === 'text' && (
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        )}
                        
                        {/* Template indicator */}
                        {msg.type === 'template' && (
                          <div className="text-sm italic text-slate-600">{msg.content}</div>
                        )}
                        
                        {/* Timestamp and status */}
                        <div className={`flex items-center justify-end gap-1 mt-1 ${
                          msg.direction === 'outgoing' ? 'text-slate-500' : 'text-slate-400'
                        }`}>
                          <span className="text-[10px]">{formatTime(msg.timestamp)}</span>
                          {msg.direction === 'outgoing' && MESSAGE_STATUS_ICONS[msg.status]}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-3 border-t bg-slate-50 flex gap-2">
                {/* File Upload */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  className="shrink-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || selectedConversation.status === 'closed'}
                  title="Attach file"
                >
                  {uploading ? (
                    <RefreshCw className="h-5 w-5 animate-spin text-slate-500" />
                  ) : (
                    <Paperclip className="h-5 w-5 text-slate-500" />
                  )}
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="icon" 
                  className="shrink-0"
                  onClick={() => { fetchTemplates(); setShowTemplateDialog(true); }}
                  disabled={selectedConversation.status === 'closed'}
                  title="Send template message"
                >
                  <FileText className="h-5 w-5 text-slate-500" />
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={selectedConversation.status === 'closed' ? 'Reopen chat to send messages' : 'Type a message...'}
                  className="flex-1"
                  disabled={sending || selectedConversation.status === 'closed'}
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending || selectedConversation.status === 'closed'}
                  className="bg-green-600 hover:bg-green-700 shrink-0"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-50">
              <div className="text-center text-slate-500">
                <MessageCircle className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium">WhatsApp Inbox</h3>
                <p className="text-sm mt-1">Select a conversation to start messaging</p>
              </div>
            </div>
          )}
          </div>

          {/* Right Panel - Customer Info, Notes, Quick Replies */}
          {selectedConversation && !isMobileView && (
            <div className={`bg-white border-l flex flex-col transition-all duration-300 ${showRightPanel ? 'w-80' : 'w-12'}`}>
              {/* Panel Toggle */}
              <div className="p-2 border-b bg-slate-50 flex items-center justify-between">
                {showRightPanel && (
                  <span className="text-sm font-medium text-slate-700">Details</span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRightPanel(!showRightPanel)}
                  className="ml-auto"
                >
                  {showRightPanel ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                </Button>
              </div>

              {showRightPanel && (
                <>
                  {/* Panel Tabs */}
                  <div className="flex border-b bg-slate-50">
                    <button
                      onClick={() => setRightPanelTab('info')}
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${rightPanelTab === 'info' ? 'bg-white border-b-2 border-green-600 text-green-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                      <Info className="h-4 w-4 mx-auto" />
                    </button>
                    <button
                      onClick={() => { setRightPanelTab('notes'); fetchNotes(selectedConversation.phone); }}
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${rightPanelTab === 'notes' ? 'bg-white border-b-2 border-green-600 text-green-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                      <StickyNote className="h-4 w-4 mx-auto" />
                    </button>
                    <button
                      onClick={() => setRightPanelTab('quick')}
                      className={`flex-1 py-2 text-xs font-medium transition-colors ${rightPanelTab === 'quick' ? 'bg-white border-b-2 border-green-600 text-green-700' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                      <Zap className="h-4 w-4 mx-auto" />
                    </button>
                  </div>

                  {/* Tab Content */}
                  <ScrollArea className="flex-1">
                    {/* Customer Info Tab */}
                    {rightPanelTab === 'info' && (
                      <div className="p-4 space-y-4">
                        {/* Customer Avatar & Name */}
                        <div className="text-center pb-4 border-b">
                          <Avatar className="h-16 w-16 mx-auto mb-2 bg-green-100">
                            <AvatarFallback className="bg-green-100 text-green-700 text-xl">
                              {getInitials(selectedConversation.contact_name || selectedConversation.customer_name)}
                            </AvatarFallback>
                          </Avatar>
                          <h3 className="font-semibold">
                            {selectedConversation.contact_name || selectedConversation.customer_name || 'Unknown'}
                          </h3>
                          <p className="text-sm text-slate-500">+{selectedConversation.phone}</p>
                        </div>

                        {/* Quick Info */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 text-sm">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-600">+{selectedConversation.phone}</span>
                          </div>
                          
                          {selectedConversation.customer_name && (
                            <div className="flex items-center gap-3 text-sm">
                              <Building2 className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-600">{selectedConversation.customer_name}</span>
                            </div>
                          )}
                          
                          {selectedConversation.assigned_to_name && (
                            <div className="flex items-center gap-3 text-sm">
                              <UserCheck className="h-4 w-4 text-slate-400" />
                              <span className="text-slate-600">{selectedConversation.assigned_to_name}</span>
                            </div>
                          )}

                          <div className="flex items-center gap-3 text-sm">
                            <Calendar className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-600">
                              Started {selectedConversation.first_message_at ? formatTime(selectedConversation.first_message_at) : 'N/A'}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 text-sm">
                            <MessageSquare className="h-4 w-4 text-slate-400" />
                            <span className="text-slate-600">{messages.length} messages</span>
                          </div>
                        </div>

                        {/* Tags Section */}
                        <div className="pt-4 border-t">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">Tags</span>
                            <Button variant="ghost" size="sm" onClick={() => setShowTagDialog(true)}>
                              <Tag className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {selectedConversation.tags && selectedConversation.tags.length > 0 ? (
                              selectedConversation.tags.map(tagId => {
                                const tag = AVAILABLE_TAGS.find(t => t.id === tagId);
                                return tag ? (
                                  <Badge key={tagId} className={`${tag.color} text-white text-xs`}>
                                    {tag.label}
                                  </Badge>
                                ) : null;
                              })
                            ) : (
                              <span className="text-xs text-slate-400">No tags</span>
                            )}
                          </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="pt-4 border-t space-y-2">
                          <span className="text-sm font-medium text-slate-700 block mb-2">Quick Actions</span>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start"
                            onClick={() => { fetchCustomers(); setShowLinkDialog(true); }}
                          >
                            <Link2 className="h-4 w-4 mr-2" />
                            {selectedConversation.customer_id ? 'Change Customer' : 'Link Customer'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start"
                            onClick={() => { fetchEmployees(); setShowAssignDialog(true); }}
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            {selectedConversation.assigned_to ? 'Reassign' : 'Assign'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full justify-start"
                            onClick={() => {
                              setTicketForm(prev => ({
                                ...prev,
                                subject: `WhatsApp inquiry from ${selectedConversation.contact_name || selectedConversation.phone}`,
                                description: generateTicketDescription()
                              }));
                              setShowTicketDialog(true);
                            }}
                          >
                            <Ticket className="h-4 w-4 mr-2" />
                            Create Ticket
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Notes Tab */}
                    {rightPanelTab === 'notes' && (
                      <div className="p-4">
                        <div className="mb-4">
                          <Textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Add a private note..."
                            rows={3}
                            className="text-sm"
                          />
                          <Button 
                            onClick={handleAddNote} 
                            disabled={!newNote.trim()}
                            size="sm"
                            className="w-full mt-2 bg-green-600 hover:bg-green-700"
                          >
                            Add Note
                          </Button>
                        </div>
                        <div className="space-y-2">
                          {notes.length === 0 ? (
                            <div className="text-center py-8 text-slate-400">
                              <StickyNote className="h-8 w-8 mx-auto mb-2" />
                              <p className="text-sm">No notes yet</p>
                            </div>
                          ) : (
                            notes.map((note, idx) => (
                              <div key={idx} className="p-2 bg-yellow-50 rounded border border-yellow-200 text-sm">
                                <p className="whitespace-pre-wrap">{note.note}</p>
                                <div className="text-xs text-slate-500 mt-1 flex justify-between">
                                  <span>{note.created_by_name?.split('@')[0]}</span>
                                  <span>{formatTime(note.created_at)}</span>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quick Replies Tab */}
                    {rightPanelTab === 'quick' && (
                      <div className="p-4">
                        {/* Create/Edit Form */}
                        {(showQuickReplyForm || editingReply) && (
                          <div className="mb-4 p-3 bg-slate-50 rounded-lg border">
                            <h4 className="text-sm font-medium mb-2">
                              {editingReply ? 'Edit Quick Reply' : 'New Quick Reply'}
                            </h4>
                            <Input
                              placeholder="Label (e.g., Greeting)"
                              value={quickReplyForm.label}
                              onChange={(e) => setQuickReplyForm(prev => ({ ...prev, label: e.target.value }))}
                              className="text-sm mb-2"
                            />
                            <Textarea
                              placeholder="Message text..."
                              value={quickReplyForm.text}
                              onChange={(e) => setQuickReplyForm(prev => ({ ...prev, text: e.target.value }))}
                              rows={3}
                              className="text-sm mb-2"
                            />
                            <div className="flex items-center gap-2 mb-3">
                              <input
                                type="checkbox"
                                id="is_shared"
                                checked={quickReplyForm.is_shared}
                                onChange={(e) => setQuickReplyForm(prev => ({ ...prev, is_shared: e.target.checked }))}
                                className="rounded"
                              />
                              <Label htmlFor="is_shared" className="text-xs flex items-center gap-1">
                                <Globe className="h-3 w-3" />
                                Share with team
                              </Label>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={editingReply ? handleUpdateQuickReply : handleCreateQuickReply}
                                className="bg-green-600 hover:bg-green-700 flex-1"
                              >
                                {editingReply ? 'Update' : 'Create'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowQuickReplyForm(false);
                                  setEditingReply(null);
                                  setQuickReplyForm({ label: '', text: '', is_shared: false });
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Add Button */}
                        {!showQuickReplyForm && !editingReply && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full mb-3"
                            onClick={() => setShowQuickReplyForm(true)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Quick Reply
                          </Button>
                        )}

                        <p className="text-xs text-slate-500 mb-2">Click to use a reply</p>

                        {/* Shared Replies */}
                        {quickReplies.shared.length > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center gap-1 mb-2 text-xs text-slate-500">
                              <Globe className="h-3 w-3" />
                              <span>Team Replies</span>
                            </div>
                            <div className="space-y-2">
                              {quickReplies.shared.map((reply) => (
                                <div
                                  key={reply.reply_id}
                                  className="p-3 rounded-lg border hover:bg-green-50 hover:border-green-300 transition-colors group relative"
                                >
                                  <button
                                    onClick={() => setNewMessage(reply.text)}
                                    className="w-full text-left"
                                    data-testid={`quick-reply-${reply.reply_id}`}
                                  >
                                    <div className="flex items-center justify-between pr-12">
                                      <span className="font-medium text-sm text-slate-700 group-hover:text-green-700">
                                        {reply.label}
                                      </span>
                                      <Zap className="h-3 w-3 text-slate-400 group-hover:text-green-600" />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{reply.text}</p>
                                  </button>
                                  {/* Edit/Delete for owner or admin */}
                                  {(reply.created_by === user?.sub || user?.roles?.includes('admin')) && (
                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100">
                                      <button
                                        onClick={() => {
                                          setEditingReply(reply);
                                          setQuickReplyForm({ label: reply.label, text: reply.text, is_shared: reply.is_shared });
                                        }}
                                        className="p-1 hover:bg-slate-200 rounded"
                                      >
                                        <Edit2 className="h-3 w-3 text-slate-500" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteQuickReply(reply.reply_id)}
                                        className="p-1 hover:bg-red-100 rounded"
                                      >
                                        <Trash2 className="h-3 w-3 text-red-500" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Personal Replies */}
                        {quickReplies.personal.length > 0 && (
                          <div className="mb-4">
                            <div className="flex items-center gap-1 mb-2 text-xs text-slate-500">
                              <Lock className="h-3 w-3" />
                              <span>My Replies</span>
                            </div>
                            <div className="space-y-2">
                              {quickReplies.personal.map((reply) => (
                                <div
                                  key={reply.reply_id}
                                  className="p-3 rounded-lg border hover:bg-blue-50 hover:border-blue-300 transition-colors group relative"
                                >
                                  <button
                                    onClick={() => setNewMessage(reply.text)}
                                    className="w-full text-left"
                                    data-testid={`quick-reply-personal-${reply.reply_id}`}
                                  >
                                    <div className="flex items-center justify-between pr-12">
                                      <span className="font-medium text-sm text-slate-700 group-hover:text-blue-700">
                                        {reply.label}
                                      </span>
                                      <Lock className="h-3 w-3 text-slate-400" />
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{reply.text}</p>
                                  </button>
                                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100">
                                    <button
                                      onClick={() => {
                                        setEditingReply(reply);
                                        setQuickReplyForm({ label: reply.label, text: reply.text, is_shared: reply.is_shared });
                                      }}
                                      className="p-1 hover:bg-slate-200 rounded"
                                    >
                                      <Edit2 className="h-3 w-3 text-slate-500" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteQuickReply(reply.reply_id)}
                                      className="p-1 hover:bg-red-100 rounded"
                                    >
                                      <Trash2 className="h-3 w-3 text-red-500" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Default Replies (if no custom ones) */}
                        {quickReplies.shared.length === 0 && quickReplies.personal.length === 0 && (
                          <div className="space-y-2">
                            <p className="text-xs text-slate-400 mb-2">Default Replies</p>
                            {DEFAULT_QUICK_REPLIES.map((reply) => (
                              <button
                                key={reply.reply_id}
                                onClick={() => setNewMessage(reply.text)}
                                className="w-full text-left p-3 rounded-lg border hover:bg-green-50 hover:border-green-300 transition-colors group"
                                data-testid={`quick-reply-default-${reply.reply_id}`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-sm text-slate-700 group-hover:text-green-700">
                                    {reply.label}
                                  </span>
                                  <Zap className="h-3 w-3 text-slate-400 group-hover:text-green-600" />
                                </div>
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{reply.text}</p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>
                </>
              )}

              {/* Collapsed state icons */}
              {!showRightPanel && (
                <div className="flex flex-col items-center gap-2 py-4">
                  <button
                    onClick={() => { setShowRightPanel(true); setRightPanelTab('info'); }}
                    className="p-2 rounded hover:bg-slate-100 text-slate-500"
                    title="Customer Info"
                  >
                    <Info className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => { setShowRightPanel(true); setRightPanelTab('notes'); fetchNotes(selectedConversation.phone); }}
                    className="p-2 rounded hover:bg-slate-100 text-slate-500"
                    title="Notes"
                  >
                    <StickyNote className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => { setShowRightPanel(true); setRightPanelTab('quick'); }}
                    className="p-2 rounded hover:bg-slate-100 text-slate-500"
                    title="Quick Replies"
                  >
                    <Zap className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {showImagePreview && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowImagePreview(null)}
        >
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={(e) => {
                e.stopPropagation();
                // Create download link
                const link = document.createElement('a');
                link.href = showImagePreview;
                link.download = `whatsapp_image_${Date.now()}.jpg`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <Download className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => setShowImagePreview(null)}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
          <img
            src={showImagePreview}
            alt="Preview"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Link Customer Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Link to Customer</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input placeholder="Search customers..." className="mb-3" />
            <ScrollArea className="h-64">
              {customers.map((customer) => (
                <div
                  key={customer.customer_id}
                  onClick={() => handleLinkCustomer(customer.customer_id)}
                  className="flex items-center gap-3 p-2 hover:bg-slate-100 rounded cursor-pointer"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                      {getInitials(customer.company_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-sm">{customer.company_name}</div>
                    <div className="text-xs text-slate-500">{customer.contact_phone}</div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Conversation</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <ScrollArea className="h-64">
              {employees.map((emp) => (
                <div
                  key={emp.user_id}
                  onClick={() => handleAssign(emp.user_id)}
                  className="flex items-center gap-3 p-2 hover:bg-slate-100 rounded cursor-pointer"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-green-100 text-green-700 text-xs">
                      {getInitials(emp.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-sm">{emp.name || emp.email}</div>
                    <div className="text-xs text-slate-500">{emp.roles?.join(', ')}</div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Internal Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Internal Notes (Private)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a private note (not visible to customer)..."
                rows={2}
                className="flex-1"
              />
              <Button onClick={handleAddNote} disabled={!newNote.trim()}>Add</Button>
            </div>
            <ScrollArea className="h-64 border rounded p-2">
              {notes.length === 0 ? (
                <div className="text-center text-slate-500 py-8">
                  <StickyNote className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No notes yet</p>
                </div>
              ) : (
                notes.map((note, idx) => (
                  <div key={idx} className="p-2 bg-yellow-50 rounded mb-2 border border-yellow-200">
                    <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                    <div className="text-xs text-slate-500 mt-1 flex justify-between">
                      <span>{note.created_by_name}</span>
                      <span>{formatTime(note.created_at)}</span>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Ticket from Chat</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={ticketForm.subject}
                onChange={(e) => setTicketForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Ticket subject"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ticket description"
                rows={6}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={ticketForm.category}
                  onValueChange={(value) => setTicketForm(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="connectivity">Connectivity</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="installation">Installation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={ticketForm.priority}
                  onValueChange={(value) => setTicketForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="include_attachments"
                checked={ticketForm.include_attachments}
                onChange={(e) => setTicketForm(prev => ({ ...prev, include_attachments: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="include_attachments" className="text-sm">Include media attachments from chat</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} className="bg-green-600 hover:bg-green-700">Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Template Message</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {templates.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                <p>No templates found</p>
                <p className="text-xs mt-1">Create templates in Meta Business Suite</p>
              </div>
            ) : (
              <ScrollArea className="h-64">
                {templates.filter(t => t.status === 'APPROVED').map((template) => (
                  <div
                    key={template.id || template.name}
                    onClick={() => setSelectedTemplate(template)}
                    className={`p-3 border rounded-lg mb-2 cursor-pointer transition-colors ${
                      selectedTemplate?.name === template.name 
                        ? 'border-green-500 bg-green-50' 
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="font-medium text-sm">{template.name}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {template.language} • {template.category}
                    </div>
                    {template.components?.find(c => c.type === 'BODY')?.text && (
                      <div className="text-xs text-slate-600 mt-2 bg-slate-100 p-2 rounded">
                        {template.components.find(c => c.type === 'BODY').text.substring(0, 150)}...
                      </div>
                    )}
                  </div>
                ))}
              </ScrollArea>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTemplateDialog(false); setSelectedTemplate(null); }}>Cancel</Button>
            <Button 
              onClick={handleSendTemplate} 
              disabled={!selectedTemplate || sending}
              className="bg-green-600 hover:bg-green-700"
            >
              {sending ? 'Sending...' : 'Send Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Management Dialog */}
      <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Manage Tags
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">Click tags to add or remove them from this conversation.</p>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_TAGS.map(tag => {
                const isActive = selectedConversation?.tags?.includes(tag.id);
                return (
                  <Badge
                    key={tag.id}
                    className={`cursor-pointer text-sm py-1 px-3 transition-all ${
                      isActive 
                        ? `${tag.color} text-white` 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => handleToggleTag(tag.id)}
                  >
                    {isActive && <Check className="h-3 w-3 mr-1" />}
                    {tag.label}
                  </Badge>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTagDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Conversations Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5" />
              Merge Phone Numbers
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              Merge multiple WhatsApp numbers into a single customer profile. Messages from all numbers will be combined.
            </p>

            {/* Auto-suggestions */}
            {mergeSuggestions.length > 0 && (
              <div className="border rounded-lg p-3 bg-amber-50 border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 mb-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Potential Duplicates Found</span>
                </div>
                <div className="space-y-2">
                  {mergeSuggestions.map((suggestion, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border">
                      <div className="font-medium text-sm">{suggestion.name}</div>
                      <div className="text-xs text-slate-500">
                        {suggestion.phones.length} numbers: {suggestion.phones.join(', ')}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => {
                          setSelectedForMerge(suggestion.phones);
                          setMergeTarget(suggestion.phones[0]);
                        }}
                      >
                        Select All
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Select conversations to merge:</Label>
              <ScrollArea className="h-48 border rounded p-2">
                {conversations.filter(c => c.status !== 'merged').map((conv) => (
                  <div
                    key={conv.phone}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                      selectedForMerge.includes(conv.phone) 
                        ? 'bg-green-50 border border-green-300' 
                        : 'hover:bg-slate-50'
                    }`}
                    onClick={() => {
                      setSelectedForMerge(prev => 
                        prev.includes(conv.phone)
                          ? prev.filter(p => p !== conv.phone)
                          : [...prev, conv.phone]
                      );
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedForMerge.includes(conv.phone)}
                      onChange={() => {}}
                      className="rounded"
                    />
                    <Avatar className="h-8 w-8 bg-green-100">
                      <AvatarFallback className="bg-green-100 text-green-700 text-xs">
                        {getInitials(conv.contact_name || conv.customer_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {conv.contact_name || conv.customer_name || conv.phone}
                      </div>
                      <div className="text-xs text-slate-500">+{conv.phone}</div>
                    </div>
                    {mergeTarget === conv.phone && (
                      <Badge className="bg-green-600 text-white text-xs">Primary</Badge>
                    )}
                  </div>
                ))}
              </ScrollArea>
            </div>

            {/* Select Primary Number */}
            {selectedForMerge.length > 1 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">Select primary number (others merge into this):</Label>
                <Select value={mergeTarget} onValueChange={setMergeTarget}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select primary number" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedForMerge.map(phone => {
                      const conv = conversations.find(c => c.phone === phone);
                      return (
                        <SelectItem key={phone} value={phone}>
                          {conv?.contact_name || conv?.customer_name || phone} (+{phone})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedForMerge.length > 1 && mergeTarget && (
              <div className="bg-slate-50 rounded p-3 text-sm">
                <strong>Summary:</strong> {selectedForMerge.length - 1} number(s) will be merged into +{mergeTarget}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowMergeDialog(false);
              setSelectedForMerge([]);
              setMergeTarget(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleMergeConversations}
              disabled={selectedForMerge.length < 2 || !mergeTarget}
              className="bg-green-600 hover:bg-green-700"
            >
              <GitMerge className="h-4 w-4 mr-2" />
              Merge {selectedForMerge.length > 1 ? `${selectedForMerge.length - 1} Numbers` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
